var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import clsx from 'clsx';
import { getDisabledElementProps, getHrefValue, getLinkProps, hasARIAHiddenValue, hasARIALabel, isNil, isString, mergeProps, shouldBrowserHandleFocus, useClasses, useConsoleLog, useLayoutProps, useProps, useSkeletonLoader, useTranslate, } from '@hawkins/helpers';
import { HawkinsGlobalClass, useTheme } from '@hawkins/styles';
import { FormGroupContext } from '../formGroup';
import { useMenuTrigger, usePress } from '../hooks';
import { Loader } from '../loader';
import { Tooltip } from '../tooltip';
import { VisuallyHidden } from '../visuallyHidden';
import { ButtonStrings } from './button.strings';
import { BUTTON_MIN_HEIGHT_SIZING_KEY, COMPACT_BUTTON_MIN_HEIGHT_SIZING_KEY, COMPACT_ICON_BUTTON_ICON_DIM_SIZING_KEY, ICON_BUTTON_ICON_DIM_SIZING_KEY, useButtonStyles, } from './button.styles';
var displayName = 'Button';
/**
 * A button initiates app-specific actions. It can affect other components, its container, or even the entire page.
 */
export var Button = React.forwardRef(function (_a, ref) {
    var _b;
    var _c, _d;
    var children = _a.children, props = __rest(_a, ["children"]);
    // Props
    var _e = useProps(displayName, props), _f = _e.ariaDisableOnly, ariaDisableOnly = _f === void 0 ? false : _f, classesProp = _e.classes, className = _e.className, _g = _e.compact, compact = _g === void 0 ? false : _g, component = _e.component, _h = _e.disabled, disabledProp = _h === void 0 ? false : _h, _j = _e.executing, executing = _j === void 0 ? false : _j, href = _e.href, Icon = _e.icon, _k = _e.iconProps, iconProps = _k === void 0 ? {} : _k, defaultTriggerId = _e.id, layout = _e.layout, loaderProps = _e.loaderProps, loading = _e.loading, 
    // TODO(API): Maybe add `props.truncate` to allow for ellipsis truncation in addition to `white-space: nowrap`.
    // Might also want to create a reusable component `TruncatableContent` with built-in `EllipsisTooltip` support
    // (verify that `EllipsisTooltip` performance is acceptable first).
    // Should also try to standardize our noWrap-related features (e.g., in `GenericFeatureProps`,
    // `props.noWrap` automatically truncates, but we might not always want those behaviors coupled).
    _l = _e.noWrap, 
    // TODO(API): Maybe add `props.truncate` to allow for ellipsis truncation in addition to `white-space: nowrap`.
    // Might also want to create a reusable component `TruncatableContent` with built-in `EllipsisTooltip` support
    // (verify that `EllipsisTooltip` performance is acceptable first).
    // Should also try to standardize our noWrap-related features (e.g., in `GenericFeatureProps`,
    // `props.noWrap` automatically truncates, but we might not always want those behaviors coupled).
    noWrap = _l === void 0 ? true : _l, onClick = _e.onClick, rootProps = _e.rootProps, styleProp = _e.style, to = _e.to, tooltipDescription = _e.tooltipDescription, tooltipLabelProp = _e.tooltipLabel, _m = _e.tooltipPlacement, tooltipPlacement = _m === void 0 ? 'top' : _m, _o = _e.tooltipProps, tooltipProps = _o === void 0 ? {} : _o, translateProp = _e.translate, _p = _e.variant, variant = _p === void 0 ? 'primary' : _p, restProps = __rest(_e, ["ariaDisableOnly", "classes", "className", "compact", "component", "disabled", "executing", "href", "icon", "iconProps", "id", "layout", "loaderProps", "loading", "noWrap", "onClick", "rootProps", "style", "to", "tooltipDescription", "tooltipLabel", "tooltipPlacement", "tooltipProps", "translate", "variant"]);
    // TODO(a11y): Improve accessibility warnings and create utility hook?
    useConsoleLog(!loading &&
        !executing &&
        isNil(children) &&
        !hasARIALabel(props) &&
        (!hasARIALabel(iconProps) || !hasARIAHiddenValue(iconProps, false)), 'An empty or icon-only `Button` should have an accessible label (give it an `aria-label`, `aria-labelledby`, or `children`).', { displayName: displayName, docsPath: 'components-button#icon' });
    // Read from the form group context, if it is inside a <FormGroup/>
    var groupContext = React.useContext(FormGroupContext);
    var disabled = (groupContext === null || groupContext === void 0 ? void 0 : groupContext.disabled) || disabledProp;
    var isColorVariant = variant === 'icon-color' || variant === 'tertiary-color';
    var isDestructive = variant === 'primary-destructive';
    var isPrimary = variant === 'primary';
    var isReverseVariant = variant === 'icon-reverse' || variant === 'tertiary-reverse';
    var isSecondary = variant === 'secondary';
    var isIconButton = variant === 'icon' || variant.startsWith('icon-');
    var canShowIcon = isIconButton || !compact;
    var isIconLarge = isIconButton && !compact;
    var isAssumedLink = getHrefValue({ href: href, to: to }) !== null;
    var Component = component || (isAssumedLink ? 'a' : 'button');
    var isNativeButton = Component === 'button';
    var tooltipLabel = (_d = (_c = tooltipProps === null || tooltipProps === void 0 ? void 0 : tooltipProps.label) !== null && _c !== void 0 ? _c : tooltipLabelProp) !== null && _d !== void 0 ? _d : (isIconButton && children ? children : undefined);
    var hasTooltip = !isNil(tooltipLabel);
    var isDisabledWithTooltip = hasTooltip && disabled;
    var _q = useMenuTrigger({ defaultTriggerId: defaultTriggerId, triggerIdNamespace: displayName }), CaretIcon = _q.CaretIcon, triggerAriaAttributes = _q.triggerAriaAttributes, id = _q.triggerId;
    var pressProps = usePress({
        alwaysHandleClick: (isAssumedLink || restProps.type === 'submit') && disabled,
        disabled: disabled,
        onPress: onClick,
    }).pressProps;
    // Strings
    var translate = useTranslate(translateProp, ButtonStrings);
    // Styles
    var baseClasses = useButtonStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var theme = useTheme();
    var executingContentContainerClassName = clsx(classes.executingContentContainer, HawkinsGlobalClass.HIDDEN);
    var rootClassName = clsx(classes.HAWKINS_BASE, HawkinsGlobalClass.FOCUSABLE, className, classes.root, (_b = {},
        _b[classes.rootColor] = isColorVariant,
        _b[classes.rootColorDisabled] = isColorVariant && disabled,
        _b[classes.rootCompact] = compact,
        _b[classes.rootDestructive] = isDestructive,
        _b[classes.rootDestructiveDisabled] = isDestructive && disabled,
        _b[classes.rootDestructiveEnabled] = isDestructive && !disabled,
        _b[classes.rootDisabled] = disabled,
        _b[classes.rootEnabled] = !disabled,
        _b[classes.rootIconButton] = isIconButton,
        _b[classes.rootIconButtonCompact] = isIconButton && compact,
        _b[classes.rootNoWrap] = noWrap,
        _b[classes.rootPrimary] = isPrimary,
        _b[classes.rootPrimaryDisabled] = isPrimary && disabled,
        _b[classes.rootPrimaryEnabled] = isPrimary && !disabled,
        _b[classes.rootReverse] = isReverseVariant,
        _b[classes.rootReverseDisabled] = isReverseVariant && disabled,
        _b[classes.rootSecondary] = isSecondary,
        _b[classes.rootSecondaryDisabled] = isSecondary && disabled,
        _b));
    // Content
    var skeletonLoaderNode = useSkeletonLoader({
        loaderProps: __assign({ height: isIconButton
                ? compact
                    ? COMPACT_ICON_BUTTON_ICON_DIM_SIZING_KEY
                    : ICON_BUTTON_ICON_DIM_SIZING_KEY
                : compact
                    ? COMPACT_BUTTON_MIN_HEIGHT_SIZING_KEY
                    : BUTTON_MIN_HEIGHT_SIZING_KEY, variant: isIconButton ? 'circle' : 'square', width: isIconButton
                ? compact
                    ? COMPACT_ICON_BUTTON_ICON_DIM_SIZING_KEY
                    : ICON_BUTTON_ICON_DIM_SIZING_KEY
                : compact
                    ? 'size6'
                    : 'size7' }, loaderProps),
        loading: loading,
        style: style,
    });
    if (skeletonLoaderNode)
        return skeletonLoaderNode;
    var buttonContentNode = (_jsxs(_Fragment, { children: [Icon && canShowIcon && (_jsx(Icon, __assign({ "aria-hidden": true, className: classes.icon, size: isIconLarge ? 'large' : 'small' }, iconProps))), isIconButton ? children && _jsx(VisuallyHidden, { children: children }) : children, CaretIcon && _jsx(CaretIcon, { "aria-hidden": true, className: classes.caret, size: "small" })] }));
    var buttonNode = (_jsx(Component, __assign({ "aria-label": executing ? translate('button.executing.ariaLabel') : undefined, className: rootClassName, id: id, ref: ref, role: isAssumedLink || isNativeButton ? undefined : 'button', style: style, tabIndex: !shouldBrowserHandleFocus(Component) &&
            // The caller might be using a custom link component that resolves to an `<a />` element (e.g., React Router `Link`).
            !(isAssumedLink && !isString(Component)) &&
            ((onClick && !disabled) || isDisabledWithTooltip)
            ? 0
            : undefined, type: 
        // Native buttons have `type="submit"` by default.
        // Set `type="button"` instead as a convenience for consumers.
        isNativeButton ? 'button' : undefined }, getDisabledElementProps(Component, disabled, {
        // TODO(a11y): Default to using `aria-disabled` for all disabled buttons and never use the `disabled` attribute
        // (see https://css-tricks.com/making-disabled-buttons-more-inclusive/)?
        // Would need to warn users in case they're relying on `disabled` attribute behavior.
        alwaysUseAria: ariaDisableOnly || isDisabledWithTooltip,
    }), getLinkProps(Component, { href: href, to: to }), triggerAriaAttributes, mergeProps(__assign(__assign({}, restProps), rootProps), pressProps), { children: executing ? (_jsxs(_Fragment, { children: [_jsx("div", __assign({ className: executingContentContainerClassName }, { children: buttonContentNode })), _jsx(Loader, { className: classes.loader, size: compact
                        ? theme.hawkinsVariables.sizingValues[COMPACT_BUTTON_MIN_HEIGHT_SIZING_KEY] -
                            theme.hawkinsVariables.spacingValues.space2
                        : theme.hawkinsVariables.sizingValues[BUTTON_MIN_HEIGHT_SIZING_KEY] -
                            theme.hawkinsVariables.spacingValues.space3 })] })) : (buttonContentNode) })));
    if (hasTooltip) {
        return (_jsx(Tooltip, __assign({ description: tooltipDescription, label: tooltipLabel, placement: tooltipPlacement }, tooltipProps, { children: buttonNode })));
    }
    return buttonNode;
});
Button.displayName = displayName;
